import React, { useMemo, useState } from "react"
import { ArrayFieldTemplateProps, ErrorSchema, IChangeEvent } from "@rjsf/core"
import { JSONSchema7 } from "json-schema"
import { Button, Text, Title, ValidationError } from "dash-styled"
import { ReactComponent as TrashIcon } from "icons/lib/trash.svg"
import { ReactComponent as IconClose } from "icons/lib/close-icon.svg"
import { ReactComponent as EditIcon } from "icons/lib/edit.svg"
import { ReactComponent as PenIcon } from "icons/lib/edit-2.svg"
import { Table } from "resha"
import { useBoolean } from "../../../../Hooks/useBoolean"
import { DataFieldType } from "../../../../Models/DataProgram/DocumentExtraction"

type SubfieldsParameterTemplateWithErrorsProps = ArrayFieldTemplateProps & {
    rawErrors?: string[]
}

type SubfieldFormProps = {
    title: string
    onClose: () => void
    onSubmit: () => void
    Field: JSX.Element
    fieldProps: {
        schema: JSONSchema7
        errorSchema: ErrorSchema
        formData?: unknown
        onChange?: (e: IChangeEvent<unknown> | any, es?: ErrorSchema) => void
    }
    submitLabel: string
}

enum FormType {
    New = "New",
    Edit = "Edit"
}

const SubfieldForm = ({
    title,
    onClose,
    onSubmit,
    Field,
    fieldProps,
    submitLabel
}: SubfieldFormProps) => {
    return (
        <>
            <div className="flex items-center justify-between px-4 py-3 bg-smoke-20 rounded-t-xl">
                <div className="flex items-center">
                    <EditIcon className="text-rhino-970 mr-3" />
                    <Title
                        variant="sm"
                        weight="semibold"
                        noMargin
                        className="text-rhino-970"
                    >
                        {title}
                    </Title>
                </div>
                <button
                    data-testid="close-button"
                    onClick={onClose}
                    className="right-0 top-0 p-1 ml-2"
                >
                    <IconClose className="w-4 h-4" />
                </button>
            </div>
            <div className="rounded-b-xl p-4 bg-smoke-20 shadow-inner">
                {React.cloneElement(Field, { ...Field.props, ...fieldProps })}
            </div>
            <Button
                className="mt-4"
                size="xs"
                variant="contained2"
                onClick={onSubmit}
            >
                {submitLabel}
            </Button>
        </>
    )
}

export const SubfieldsParameterTemplate = ({
    readonly,
    disabled,
    canAdd,
    rawErrors,
    formData,
    registry,
    formContext,
    items = [],
    onAddClick
}: SubfieldsParameterTemplateWithErrorsProps) => {
    const [showForm, openForm, closeForm] = useBoolean(false)

    const [errorSchema, setErrorSchema] = useState<ErrorSchema>({})

    const [subfieldFormState, setSubfieldFormState] =
        useState<{ type: FormType; index: number }>()

    const [selectedSubfield, setSelectedSubfield] = useState<unknown>()

    const tableItems = useMemo(
        () =>
            subfieldFormState?.type === "New"
                ? formData.filter(
                      (el: unknown, index: number) =>
                          index !== subfieldFormState?.index
                  )
                : formData,
        [formData, subfieldFormState?.index, subfieldFormState?.type]
    )

    const closeSubfieldForm = (type: FormType) => () => {
        closeForm()
        if (type === FormType.New) {
            const index = subfieldFormState?.index as number
            items?.[index].onDropIndexClick(index)()
        }

        setSelectedSubfield(undefined)
        setSubfieldFormState(undefined)
    }

    const validateForm = (formData: unknown, schema: JSONSchema7) => {
        return formContext.ref.current.validate(formData, schema)
    }

    const handleSubmitSubfieldForm = () => {
        const index = subfieldFormState?.index as number
        const { errorSchema, errors } = validateForm(selectedSubfield, {
            ...items?.[index]?.children?.props?.schema,
            definitions: registry.definitions
        })

        if (errors.length) {
            setErrorSchema(errorSchema)
        } else {
            setErrorSchema({})
            formData.splice(index, 1, selectedSubfield)
            registry.formContext.onChange(formData)
            closeForm()
            setSubfieldFormState(undefined)
            setSelectedSubfield(undefined)
        }
    }

    const handleDeleteSubfield = (index: number) => () => {
        items?.[index].onDropIndexClick(index)()
    }

    const handleFormChange = (value: unknown) => {
        setSelectedSubfield(value)
    }

    const handleShowForm = (type: FormType, index: number) => () => {
        setSubfieldFormState({
            type,
            index
        })
        setErrorSchema({})
        if (type === FormType.New) {
            onAddClick()
        } else {
            setSelectedSubfield(formData[index])
        }
        openForm()
    }

    return (
        <div>
            {Boolean(formData.length) && (
                <Table
                    className="-ml-4 my-4"
                    style={{
                        width: "calc(100% + 2rem)"
                    }}
                    getKey={(field: DataFieldType) => field?.fieldId?.data}
                    items={tableItems}
                    rowBgColor=""
                    columns={[
                        {
                            className: "w-1/2 py-0 pl-4",
                            headerClassName: "bg-white",
                            renderHeader: () => null,
                            render: ({ fieldName }) => {
                                return (
                                    <>
                                        <Title
                                            component="h4"
                                            variant="heading4"
                                            className="capitalize m-0"
                                        >
                                            {fieldName.data}
                                        </Title>
                                    </>
                                )
                            }
                        },
                        {
                            className: "w-2/6 py-0",
                            headerClassName: "bg-white",
                            renderHeader: () => null,
                            render: ({ options }) => {
                                return <Text>{options.data?.type}</Text>
                            }
                        },
                        {
                            className: "w-1/6 py-0 pr-4",
                            headerClassName: "bg-white",
                            renderHeader: () => null,
                            render: (item, isChecked, rowIndex) => {
                                return (
                                    !disabled &&
                                    !readonly && (
                                        <div className="flex items-center justify-around border-l">
                                            <Button
                                                disabled={showForm}
                                                className="w-auto"
                                                variant="text"
                                                marginLeft="2"
                                                onClick={handleShowForm(
                                                    FormType.Edit,
                                                    rowIndex
                                                )}
                                            >
                                                <PenIcon />
                                            </Button>
                                            <Button
                                                disabled={showForm}
                                                className="w-auto"
                                                variant="text"
                                                marginLeft="2"
                                                onClick={handleDeleteSubfield(
                                                    rowIndex
                                                )}
                                            >
                                                <TrashIcon className="w-5 h-5" />
                                            </Button>
                                        </div>
                                    )
                                )
                            }
                        }
                    ]}
                />
            )}
            {rawErrors?.map((error, i) => (
                <ValidationError key={i} className="mr-1">
                    {error === "is a required property"
                        ? "Please fill this out"
                        : error}
                </ValidationError>
            ))}
            {canAdd && !disabled && !readonly && (
                <>
                    {showForm &&
                    subfieldFormState &&
                    Number.isInteger(subfieldFormState?.index) ? (
                        <SubfieldForm
                            title={
                                subfieldFormState.type === "New"
                                    ? "Add new subfield"
                                    : "Edit subfield"
                            }
                            submitLabel={
                                subfieldFormState.type === "New"
                                    ? "Add subfield"
                                    : "Update subfield"
                            }
                            onSubmit={handleSubmitSubfieldForm}
                            onClose={closeSubfieldForm(subfieldFormState.type)}
                            Field={items[subfieldFormState.index]?.children}
                            fieldProps={{
                                errorSchema,
                                formData:
                                    subfieldFormState.type === FormType.New
                                        ? selectedSubfield
                                            ? selectedSubfield
                                            : subfieldFormState
                                            ? formData[subfieldFormState.index]
                                            : undefined
                                        : selectedSubfield,
                                onChange: handleFormChange,
                                schema: {
                                    ...items[subfieldFormState.index]?.children
                                        ?.props?.schema,
                                    definitions: registry.definitions
                                }
                            }}
                        />
                    ) : (
                        <Button
                            className="mt-4"
                            size="xs"
                            color="plain"
                            variant="contained2"
                            onClick={handleShowForm(
                                FormType.New,
                                formData.length
                            )}
                        >
                            Add new subfield
                        </Button>
                    )}
                </>
            )}
        </div>
    )
}
